import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone, Truck, Menu, X } from "lucide-react";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#manifesto" },
  { label: "Our Work", href: "#gallery" },
  { label: "Reviews", href: "#reviews" },
  { label: "Free Quote", href: "#quote" },
];

const scrollTo = (href) => (e) => {
  e.preventDefault();
  if (window.__lenis) {
    window.__lenis.scrollTo(href, { offset: -72 });
  } else {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  }
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "border-b border-[#282C34] bg-[#0B0C0E]/85 backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <a
          href="#top"
          onClick={scrollTo("#top")}
          data-testid="nav-logo"
          className="flex items-center gap-3"
        >
          <span className="flex h-10 w-10 items-center justify-center bg-[#FF5A00] text-[#0B0C0E]">
            <Truck className="h-5 w-5" strokeWidth={2.5} />
          </span>
          <span className="font-display text-2xl tracking-wider leading-none">
            GW JUNK <span className="text-[#FF5A00]">SOLUTIONS</span>
          </span>
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={scrollTo(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="font-mono-ind text-xs uppercase tracking-[0.2em] text-zinc-400 transition-colors duration-300 hover:text-[#FF9F00]"
            >
              {l.label}
            </a>
          ))}
          <a
            href="tel:+19183997313"
            data-testid="nav-call-button"
            className="group flex items-center gap-2 border border-[#FF5A00]/60 bg-[#FF5A00]/10 px-4 py-2.5 font-mono-ind text-xs font-bold uppercase tracking-widest text-[#FF9F00] transition-all duration-300 hover:bg-[#FF5A00] hover:text-[#0B0C0E]"
          >
            <Phone className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-12" />
            918-399-7313
          </a>
        </nav>

        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="flex h-10 w-10 items-center justify-center border border-[#282C34] text-zinc-300 lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <motion.nav
          data-testid="nav-mobile-menu"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="border-t border-[#282C34] bg-[#0B0C0E]/95 backdrop-blur-xl lg:hidden"
        >
          <div className="flex flex-col gap-1 px-5 py-4">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => {
                  scrollTo(l.href)(e);
                  setOpen(false);
                }}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="border-b border-[#1a1d23] py-3 font-mono-ind text-sm uppercase tracking-[0.2em] text-zinc-300"
              >
                {l.label}
              </a>
            ))}
          </div>
        </motion.nav>
      )}
    </motion.header>
  );
}
